import { useState, useCallback } from "react";
import { SimCanvas } from "@/components/SimCanvas";
import { createBall, DEFAULT_CONFIG } from "@/lib/physics";
import type { Ball, PhysicsConfig } from "@/lib/physics";
import { Button } from "@/components/ui/button";

export function Lesson2Elasticity() {
	const [restitution, setRestitution] = useState(0.9);
	const [balls, setBalls] = useState<Ball[]>(() => [
		createBall(200, 60, 100, 0, 22),
		createBall(500, 60, -100, 0, 22),
	]);
	const [config, setConfig] = useState<PhysicsConfig>({
		...DEFAULT_CONFIG,
		restitution: 0.9,
	});

	const reset = (r: number) => {
		setRestitution(r);
		setConfig((prev) => ({ ...prev, restitution: r }));
		setBalls([
			createBall(200, 60, 100, 0, 22),
			createBall(500, 60, -100, 0, 22),
		]);
	};

	const handleClick = useCallback((x: number, y: number) => {
		const vx = (Math.random() - 0.5) * 400;
		const vy = (Math.random() - 0.5) * 200;
		setBalls((prev) => [...prev, createBall(x, y, vx, vy, 15 + Math.random() * 15)]);
	}, []);

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<div className="flex items-center gap-4">
					<label className="text-sm font-medium text-foreground min-w-[140px]">
						Bounciness: <span className="text-cyan-400">{(restitution * 100).toFixed(0)}%</span>
					</label>
					<input
						type="range"
						min="0"
						max="100"
						value={restitution * 100}
						onChange={(e) => {
							const r = Number(e.target.value) / 100;
							setRestitution(r);
							setConfig((prev) => ({ ...prev, restitution: r }));
						}}
						className="flex-1 accent-cyan-400"
					/>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => reset(0)}>
						Clay (0%)
					</Button>
					<Button variant="outline" size="sm" onClick={() => reset(0.5)}>
						Tennis Ball (50%)
					</Button>
					<Button variant="outline" size="sm" onClick={() => reset(0.9)}>
						Rubber (90%)
					</Button>
					<Button variant="outline" size="sm" onClick={() => reset(1.0)}>
						Perfect (100%)
					</Button>
				</div>
			</div>

			<SimCanvas
				balls={balls}
				config={config}
				showTrails
				showEnergy
				onClick={handleClick}
			/>

			<p className="text-sm text-muted-foreground">
				<strong>Coefficient of Restitution</strong> determines how much energy is kept after a bounce. 100% = perfectly elastic (no energy lost), 0% = perfectly inelastic (all energy absorbed). Click to add more balls!
			</p>
		</div>
	);
}
